import { boolean, number, object, string } from 'yup'
import { MultiStepSchema, StepSchema } from './Types'
import { sourceVisualElementId } from './shared/sourceVisualElementId'
import { Design } from 'blaze-ui/icons'
import { brandVoiceId, tone } from './shared/voiceTones'
import { sourceMaterialIds } from './shared/sourceMaterials'
import { filename } from './shared/filename'
import { brandVisualKitId } from './shared/brandVisualKit'
import { safify } from './utils/safify'
import { DesignChannel } from 'components/NewFileModal/Types'
import { designTopic } from './shared/designTopic'
import { applyBrand } from './shared/applyBrand'
import { designProject } from './shared/designProject'
import { language } from './shared/language'
import { crossPostAccountsFromDesign } from './shared/crossPostAccounts'
import { caption } from './shared/caption'
import { DesignChannelsNames } from 'common/ShareSheetV2/Screens/constants'
import { VisualChannelKey } from 'types'
import { startCase } from 'lodash'
import { brandKitId } from './shared/brandKitId'
import { designContent } from './shared/designContent'
import { ChannelType } from 'components/CampaignCreationModal/Types'

const UNSUPPORTED_CROSSPOST_CHANNELS = ['tiktok_post']

export const ChooseTemplateSchema = object({
  source_visual_element_id: sourceVisualElementId,
}).meta({
  title: 'Choose Template',
  submitLabel: 'Next',
})

export const AddPhotoSchema = object({
  initial_media_file_id: number().default(undefined).meta({ label: 'Add Photo' }).transform(safify),
  initial_media_file_remove_background: boolean().default(false).meta({ hidden: true }),
}).meta({
  title: 'Add Photo',
  submitLabel: 'Next',
}) as StepSchema

export const getCustomizeSchemaWithCaption = ({ generationType }: { generationType: string }) =>
  object({
    brand_visual_kit_id: brandVisualKitId,
    apply_brand: applyBrand,
    topic: designTopic,
    brand_voice_id: brandVoiceId.optional(),
    tone: tone.optional().meta({ generationType }),
    source_material_ids: sourceMaterialIds.optional(),
    brand_kit_id: brandKitId,
    filename: filename,
    project: designProject,
    ...(!UNSUPPORTED_CROSSPOST_CHANNELS.includes(generationType) && {
      cross_post_accounts: crossPostAccountsFromDesign,
    }),
    language,
  }).meta({
    title: 'Customize',
    submitLabel: 'Generate',
  }) as StepSchema

export const CustomizeSchemaWithoutCaption = object({
  brand_visual_kit_id: brandVisualKitId,
  apply_brand: applyBrand,
  brand_kit_id: brandKitId,
  filename: filename,
  project: designProject,
  cross_post_accounts: crossPostAccountsFromDesign,
}).meta({
  title: 'Customize',
  submitLabel: 'Generate',
}) as StepSchema

export const SelectImageSchema = object({
  initial_media_file_id: number().default(undefined).meta({ label: 'Add Photo' }).transform(safify),
}).meta({
  title: 'Select Image',
  submitLabel: 'Generate Post',
}) as StepSchema

export const UploadVideoSchema = object({
  initial_media_file_id: number().default(undefined).meta({ label: 'Upload Video' }).transform(safify),
}).meta({
  title: 'Upload Video',
  submitLabel: 'Generate Post',
}) as StepSchema

const getVideoCaptionSchema = ({ generationType }: { generationType: string }) =>
  object({
    topic: designTopic
      .meta({
        label: `What is your ${startCase(DesignChannelsNames[generationType as VisualChannelKey])} about?`,
        generationType,
      })
      .required(),
  }).meta({
    title: 'Context',
    submitLabel: 'Generate Post',
  }) as StepSchema

export const ChooseVideoTemplateSchema = object({
  source_visual_element_id: sourceVisualElementId.required(),
  filename: filename,
}).meta({
  title: 'Pick Your Template',
  submitLabel: 'Use Selected Template',
}) as StepSchema

const getPurposeAndGoalsSchema = ({
  generationType,
  textPostEntrypoint,
}: {
  generationType: string
  textPostEntrypoint?: ChannelType
}) =>
  object({
    topic: designTopic
      .meta({
        label: `What is your ${startCase(DesignChannelsNames[generationType as VisualChannelKey])} about?`,
        generationType,
      })
      .required(),
    brand_kit_id: brandKitId,
    apply_brand: applyBrand,
    brand_visual_kit_id: brandVisualKitId.meta({ label: 'Brand Style' }),
    brand_voice_id: brandVoiceId.optional().meta({ label: 'Brand Voice' }),
    tone: tone.optional().meta({ generationType }),
    source_material_ids: sourceMaterialIds.optional().meta({ label: 'Source Materials' }),
    language,
  }).meta({
    title: 'Purpose & Goals',
    submitLabel: 'Generate Post Details',
    header: 'Set purpose and goals',
    textPostEntrypoint,
  }) as StepSchema

export const getTemplateAndDetailsSchemaWithCaption = ({
  generationType,
  textPostEntrypoint,
}: {
  generationType: string
  textPostEntrypoint?: ChannelType
}) =>
  object({
    source_visual_element_id: sourceVisualElementId.required(),
    content: designContent,
    caption: caption.meta({ generationType }),
    filename,
    project: designProject,
    ...(!UNSUPPORTED_CROSSPOST_CHANNELS.includes(generationType) && {
      cross_post_accounts: crossPostAccountsFromDesign,
    }),
  }).meta({
    title: 'Confirm Details',
    submitLabel: 'Choose Your Image',
    header: 'Refine post details',
    textPostEntrypoint,
  })

const TemplateAndDetailsSchemaWithoutCaption = object({
  source_visual_element_id: sourceVisualElementId.required(),
  filename: filename,
  project: designProject,
  cross_post_accounts: crossPostAccountsFromDesign,
}).meta({
  title: 'Confirm Details',
  submitLabel: 'Choose Your Image',
})

export const getBaseDesignSchemaWithCaption = ({
  generationType,
  textPostEntrypoint,
}: {
  generationType: string
  textPostEntrypoint?: ChannelType
}) =>
  ({
    meta: {
      channel: DesignChannel.Post,
      title: 'Design Wizard',
      previewUrl: '',
      icon: Design,
    },
    steps: [
      getPurposeAndGoalsSchema({ generationType, textPostEntrypoint }),
      getTemplateAndDetailsSchemaWithCaption({ generationType, textPostEntrypoint }),
      SelectImageSchema,
    ],
  } as MultiStepSchema)

export const BaseDesignSchemaWithoutCaption = ({ generationType }: { generationType: string }) =>
  ({
    meta: {
      channel: DesignChannel.Post,
      title: 'Design Wizard',
      previewUrl: '',
      icon: Design,
    },
    steps: [getPurposeAndGoalsSchema({ generationType }), TemplateAndDetailsSchemaWithoutCaption, SelectImageSchema],
  } as MultiStepSchema)

const MarketplaceTemplateSchema = object({
  source_visual_element_id: sourceVisualElementId.required(),
  brand_visual_kit_id: brandVisualKitId.optional().meta({ label: 'Style' }),
  filename: filename,
  project: designProject.meta({ label: 'Name your post and select a location' }),
}).meta({
  title: 'Template',
  submitLabel: 'Generate',
})

const MarketplaceSelectImageSchema = object({
  initial_media_file_id: string().default(undefined).meta({ label: 'Upload your image' }).required(),
}).meta({
  title: 'Image',
  submitLabel: 'Use Selected Image',
}) as StepSchema

export const MarketplaceSelectImageWithNameSchema = object({
  initial_media_file_id: string().default(undefined).meta({ label: 'Upload your image' }).required(),
  filename,
}).meta({
  title: 'Image',
  submitLabel: 'Use Selected Image',
}) as StepSchema

const MarketplaceRemoveBackgroundSchema = object({
  initial_media_file_remove_background: boolean().default(true).meta({ hidden: true }),
}).meta({
  title: 'Background',
  submitLabel: 'Pick a Template',
}) as StepSchema

export const BaseMarketplaceDesignSchema = () =>
  ({
    meta: {
      channel: DesignChannel.Post,
      title: 'Design Wizard',
      previewUrl: '',
      icon: Design,
    },
    steps: [MarketplaceSelectImageSchema, MarketplaceRemoveBackgroundSchema, MarketplaceTemplateSchema],
  } as MultiStepSchema)

export const BaseVideoDesignSchema = ({ designChannel }: { designChannel: DesignChannel }) =>
  ({
    meta: {
      channel: designChannel,
      title: 'Design Wizard',
      previewUrl: '',
      icon: Design,
      isVideo: true,
    },
    steps: [UploadVideoSchema, getVideoCaptionSchema({ generationType: designChannel }), ChooseVideoTemplateSchema],
  } as MultiStepSchema)
